import { type Color, type Direction, PAWN, type PieceType } from "src/engine/rules/board";
import type { StructureKind } from "src/engine/rules/industry/state";
import { type Move, moveTarget } from "src/engine/rules/move";
import type { CarrySpec } from "src/api";
import { asSide, asSquare, DIRECTION_NAME, PIECE_NAME, STRUCTURE_NAME } from "src/engine/api/names";

import type { Forging, Placing } from "./types";

/**
 * What the player has picked up and is dragging.
 *
 * **Ergonomics and nothing else.** A carry is a click drawn out over a drag:
 * its targets are the squares the board has already lit, and letting go on one
 * is `pickSquare` on it. So there is no rule in here, and nothing a carry can
 * do that two clicks cannot — which is what lets one gesture cover a move, a
 * forge, a build and a token without four implementations of what each means.
 */
export type Carry =
  | { kind: "piece"; from: number }
  | { kind: "forge"; type: PieceType }
  | { kind: "build"; structure: StructureKind }
  | { kind: "token"; structure: StructureKind };

/** Everything a carry's targets are read off — the click flow's own state. */
export interface CarryInputs {
  selected: number | undefined;
  movesFromSelected: readonly Move[];
  forging: Forging | undefined;
  placing: Placing | undefined;
  localSide: Color;
  mintFacing: Direction | null;
}

/**
 * The carry as the board draws it, or undefined once what it stood on is gone
 * — the piece deselected, the tool put down by Escape. **Derived, so a carry
 * can never offer a square a click would not take.**
 */
export function carrySpec(carry: Carry | undefined, inputs: CarryInputs): CarrySpec | undefined {
  if (!carry) return undefined;
  const { selected, movesFromSelected, forging, placing, localSide, mintFacing } = inputs;
  switch (carry.kind) {
    case "piece":
      if (selected !== carry.from || movesFromSelected.length === 0) return undefined;
      return {
        ghost: { kind: "held", square: asSquare(carry.from) },
        // A promotion is four moves onto one square.
        targets: [...new Set(movesFromSelected.map(moveTarget))].map(asSquare)
      };
    case "forge":
      if (forging?.type !== carry.type) return undefined;
      return {
        ghost: {
          kind: "piece",
          type: PIECE_NAME[carry.type],
          side: asSide(localSide),
          ...(carry.type === PAWN && mintFacing !== null ? { facing: DIRECTION_NAME[mintFacing] } : {})
        },
        targets: forging.at.map(asSquare)
      };
    case "build":
      if (forging?.build !== carry.structure) return undefined;
      return {
        ghost: { kind: "structure", structure: STRUCTURE_NAME[carry.structure], owner: asSide(localSide) },
        targets: forging.at.map(asSquare)
      };
    case "token":
      if (placing?.kind !== carry.structure) return undefined;
      return {
        ghost: { kind: "structure", structure: STRUCTURE_NAME[carry.structure], owner: asSide(localSide) },
        targets: placing.at.map(asSquare)
      };
  }
}
